import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Camera, Search, HandHeart, BarChart3, MapPin, Filter, Sparkles, RotateCcw } from 'lucide-react';
import { TeamAApi, A_CATEGORIES, A_FILTER_GROUPS, type LostItem } from '@ai-tiaozs/shared';
import { SafeImage } from '@/components/SafeImage';

const STEPS = [
  { id: 'publish', name: '发布失物', desc: '拍照 + 选类别 + 固定存放点', icon: Camera, owner: '张翰天' },
  { id: 'search', name: '智能寻找', desc: '文字/语音/参考图', icon: Search, owner: '肖淑慧' },
  { id: 'pickup', name: '自助领取', desc: '到失物点核对 + 我已取回', icon: HandHeart, owner: '高宇棋' },
  { id: 'stats', name: '互助报告', desc: '找回率 + 寻找反馈', icon: BarChart3, owner: '高宇棋' },
];

/** 状态显示标签(2.0 五状态) */
const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  available: { label: '等待寻找', cls: 'bg-teamA-warning/10 text-teamA-warning' },
  returned: { label: '已找回', cls: 'bg-teamA-safe/10 text-teamA-safe' },
  expired: { label: '已过期', cls: 'bg-slate-100 text-slate-500' },
};

/**
 * A 队 · 小物归队 首页(失物墙)
 * 责任学生:张翰天
 * 结构: 作品流程入口 + 公开失物墙(5 状态筛选 + 类别筛选)
 */
export function TeamAHome() {
  const [items, setItems] = useState<LostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<string>('all');
  const [group, setGroup] = useState<string>('all');
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    TeamAApi.listItems({
      category: category === 'all' ? undefined : category,
      group: group === 'all' ? undefined : group,
    })
      .then((d) => setItems(d.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [category, group]);

  const handleSeed = async () => {
    setSeeding(true);
    setSeedMsg(null);
    try {
      const r = await TeamAApi.seed();
      setSeedMsg(`已灌入 ${r.inserted} 件演示数据`);
      load();
    } catch (e) {
      setSeedMsg(`失败: ${(e as Error).message}`);
    } finally {
      setSeeding(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('确认清空 A 队所有失物/认领/归还数据?此操作不可撤销。')) return;
    setSeeding(true);
    setSeedMsg(null);
    try {
      await TeamAApi.reset();
      setSeedMsg('已清空所有数据');
      load();
    } catch (e) {
      setSeedMsg(`失败: ${(e as Error).message}`);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-5">
      <header className="card flex items-center gap-3">
        <Shield className="h-8 w-8 text-teamA-primary" />
        <div>
          <h1 className="font-bold">小物归队</h1>
          <p className="text-xs text-slate-500">AI 校园失物寻回智能体 · 张翰天 · 肖淑慧 · 高宇棋</p>
        </div>
      </header>

      {/* 作品流程入口 */}
      <section className="card">
        <h2 className="text-sm font-semibold text-slate-700">作品流程</h2>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.id}
                to={`/${s.id}`}
                className="card flex flex-col items-center gap-1 p-3 text-center hover:shadow-md"
              >
                <span className="badge bg-teamA-primary/10 text-teamA-primary">第 {idx + 1} 步</span>
                <Icon className="h-5 w-5 text-teamA-primary" />
                <span className="text-sm font-medium">{s.name}</span>
                <span className="text-[11px] leading-tight text-slate-500">{s.desc}</span>
                <span className="text-[10px] text-slate-400">责任:{s.owner}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 公开失物墙 */}
      <section className="card">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">公开失物墙</h2>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-400">{items.length} 件</span>
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="flex items-center gap-1 rounded-md bg-teamB-primary/10 px-2 py-1 text-[10px] text-teamB-primary hover:bg-teamB-primary/20 disabled:opacity-50"
              title="灌入 6 件演示失物(含 3 个外观相近的蓝色水杯)"
            >
              <Sparkles className="h-3 w-3" />
              {seeding ? '处理中…' : '演示数据'}
            </button>
            <button
              onClick={handleReset}
              disabled={seeding}
              className="flex items-center gap-1 rounded-md bg-teamA-danger/10 px-2 py-1 text-[10px] text-teamA-danger hover:bg-teamA-danger/20 disabled:opacity-50"
              title="清空所有失物/认领/归还数据"
            >
              <RotateCcw className="h-3 w-3" />
              清空
            </button>
          </div>
        </div>
        {seedMsg && (
          <p className="mt-2 rounded-md bg-teamB-primary/10 px-2 py-1 text-[11px] text-teamB-primary">
            {seedMsg}
          </p>
        )}

        {/* 状态筛选(2.0 五状态分组) */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <Filter className="h-3 w-3 text-slate-400" />
          <span className="text-slate-500">状态:</span>
          {A_FILTER_GROUPS.map((g) => (
            <button
              key={g.value}
              className={`badge ${group === g.value ? 'bg-teamA-primary text-white' : 'bg-slate-100 text-slate-700'}`}
              onClick={() => setGroup(g.value)}
            >
              {g.label}
            </button>
          ))}
        </div>

        {/* 类别筛选 */}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">类别:</span>
          <button
            className={`badge ${category === 'all' ? 'bg-teamA-primary text-white' : 'bg-slate-100 text-slate-700'}`}
            onClick={() => setCategory('all')}
          >
            全部
          </button>
          {A_CATEGORIES.map((c) => (
            <button
              key={c}
              className={`badge ${category === c ? 'bg-teamA-primary text-white' : 'bg-slate-100 text-slate-700'}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-3 space-y-2">
          {loading && <p className="text-xs text-slate-500">失物墙加载中…</p>}
          {!loading && items.length === 0 && (
            <div className="rounded-md bg-slate-50 px-3 py-4 text-center text-sm text-slate-500">
              失物墙是空的。可以:
              <ul className="mt-1 text-xs">
                <li>点【演示数据】按钮灌入 6 件测试失物</li>
                <li>或点【发布失物】手动添加</li>
              </ul>
            </div>
          )}
          {items.map((it) => {
            const sl = STATUS_LABEL[it.status] ?? STATUS_LABEL.available!;
            return (
              <Link
                key={it.id}
                to={`/item/${it.id}`}
                className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3 hover:shadow"
              >
                <SafeImage
                  team="a"
                  imageUrl={it.imageUrl}
                  imageKey={it.imageKey}
                  alt={it.title}
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="badge bg-slate-200 text-slate-700">{it.category}</span>
                    <span className={`badge ${sl.cls}`}>{sl.label}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium">{it.title}</p>
                  {it.foundLocation && (
                    <p className="text-[11px] text-slate-500">
                      <MapPin className="mr-1 inline h-3 w-3" />
                      捡到于 {it.foundLocation}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
